import { useMutation, useQuery, useQueryClient } from "react-query";
import { QueryReturnType } from "../../services/networking.service";
import { Auth } from "../../interface";
import { mutatePutFn } from "../../services/networking.service";
import ENDPOINTS from "../../services/endPoints";
import { toast } from "react-toastify";

interface FavouritesResponse {
  data?: { favourites?: string[] };
}

export const useGetFavouriteLinks = (): QueryReturnType<FavouritesResponse> => {
  return useQuery<Auth, never, any>([ENDPOINTS.GET_FAVOURITE_LINKS], {
    // Inherits the app's default retry (1, with backoff) rather than
    // overriding to false — every optimistic favourite toggle is computed
    // against whatever this query last returned, so a transient failure on
    // the very first fetch (no automatic retry, no error UI) would leave the
    // client permanently believing the favourites list is empty/stale until
    // some other action happens to trigger a fresh fetch.
    refetchOnWindowFocus: false,
    staleTime: 0,
    cacheTime: 300000,
    notifyOnChangeProps: ["data", "error", "refetch", "isRefetching", "isSuccess", "isLoading"],
  });
};

/**
 * Optimistic toggle — the star flips instantly rather than waiting on a
 * round trip, with a rollback if the request actually fails. Low-stakes
 * personal preference, so this bias toward responsiveness is worth it.
 */
export const useToggleFavouriteLink = () => {
  const queryClient = useQueryClient();
  const queryKey = [ENDPOINTS.GET_FAVOURITE_LINKS];

  return useMutation<Auth, never, { path: string }>(
    ({ path }) => mutatePutFn(ENDPOINTS.TOGGLE_FAVOURITE_LINK, { path }),
    {
      onMutate: async ({ path }) => {
        await queryClient.cancelQueries(queryKey);
        const previous = queryClient.getQueryData<FavouritesResponse>(queryKey);
        const current = previous?.data?.favourites || [];
        const next = current.includes(path) ? current.filter((p) => p !== path) : [...current, path];
        queryClient.setQueryData<FavouritesResponse>(queryKey, { data: { favourites: next } });
        return { previous };
      },
      onError: (err: any, _vars, context: any) => {
        if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
        // The optimistic update makes failures invisible otherwise — the star
        // would just silently revert with no indication anything went wrong.
        toast.error(err?.response?.data?.message || "Couldn't update favourites — try again");
      },
      onSettled: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );
};
